class AnnurMegaMenu extends HTMLElement {
  connectedCallback() {
    this.menus = [...this.querySelectorAll('.annur-mega')];
    this.closeTimers = new Map();
    this.handleToggle = this.handleToggle.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handlePreviewIntent = this.handlePreviewIntent.bind(this);
    this.hoverQuery = window.matchMedia('(min-width: 990px) and (hover: hover)');
    this.menus.forEach((menu) => {
      menu.addEventListener('toggle', this.handleToggle);
      menu.addEventListener('pointerenter', this.handlePointerEnter);
      menu.addEventListener('pointerleave', this.handlePointerLeave);
      menu.addEventListener('focusin', this.handleFocusIn);
      menu.addEventListener('focusout', this.handleFocusOut);
      menu.querySelector('summary')?.setAttribute('aria-expanded', String(menu.open));
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.closeAll();
    });
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('pointerover', this.handlePreviewIntent);
    this.addEventListener('focusin', this.handlePreviewIntent);
  }
  disconnectedCallback() {
    this.closeTimers?.forEach((timer) => window.clearTimeout(timer));
    this.closeTimers?.clear();
    this.menus?.forEach((menu) => {
      menu.removeEventListener('toggle', this.handleToggle);
      menu.removeEventListener('pointerenter', this.handlePointerEnter);
      menu.removeEventListener('pointerleave', this.handlePointerLeave);
      menu.removeEventListener('focusin', this.handleFocusIn);
      menu.removeEventListener('focusout', this.handleFocusOut);
    });
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('pointerover', this.handlePreviewIntent);
    this.removeEventListener('focusin', this.handlePreviewIntent);
  }
  handleToggle(event) {
    const menu = event.currentTarget;
    menu.querySelector('summary')?.setAttribute('aria-expanded', String(menu.open));
    if (menu.open)
      this.menus
        .filter((item) => item !== menu)
        .forEach((item) => {
          item.open = false;
        });
  }
  handleDocumentClick(event) {
    if (!this.contains(event.target)) this.closeAll();
  }
  handlePointerEnter(event) {
    if (!this.hoverQuery.matches) return;
    this.cancelClose(event.currentTarget);
    this.openMenu(event.currentTarget);
  }
  handlePointerLeave(event) {
    if (!this.hoverQuery.matches || event.currentTarget.contains(document.activeElement)) return;
    if (event.currentTarget.contains(event.relatedTarget)) return;
    this.scheduleClose(event.currentTarget);
  }
  handleFocusIn(event) {
    this.openMenu(event.currentTarget);
  }
  handleFocusOut(event) {
    const menu = event.currentTarget;
    window.setTimeout(() => {
      if (!menu.contains(document.activeElement) && !menu.matches(':hover')) menu.open = false;
    }, 0);
  }
  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    const openMenu = this.menus.find((menu) => menu.open);
    if (openMenu) {
      openMenu.open = false;
      openMenu.querySelector('summary')?.focus();
    }
  }
  handlePreviewIntent(event) {
    const column = event.target.closest('[data-annur-mega-key]');
    if (!column || !this.contains(column)) return;
    const panel = column.closest('.annur-mega__panel');
    const key = column.dataset.annurMegaKey;
    if (!panel || !key) return;

    panel.querySelectorAll('[data-annur-mega-scene]').forEach((scene) => {
      scene.hidden = scene.dataset.annurMegaScene !== key;
    });
    panel.querySelectorAll('[data-annur-mega-key]').forEach((item) => {
      item.classList.toggle('is-active', item === column);
    });
  }
  closeAll() {
    this.menus.forEach((menu) => {
      this.cancelClose(menu);
      menu.open = false;
    });
  }
  openMenu(menu) {
    this.cancelClose(menu);
    this.menus
      .filter((item) => item !== menu)
      .forEach((item) => {
        this.cancelClose(item);
        item.open = false;
      });
    menu.open = true;
  }
  scheduleClose(menu) {
    this.cancelClose(menu);
    const timer = window.setTimeout(() => {
      if (!menu.matches(':hover') && !menu.contains(document.activeElement)) menu.open = false;
      this.closeTimers.delete(menu);
    }, 260);
    this.closeTimers.set(menu, timer);
  }
  cancelClose(menu) {
    const timer = this.closeTimers?.get(menu);
    if (timer) window.clearTimeout(timer);
    this.closeTimers?.delete(menu);
  }
}
if (!customElements.get('annur-mega-menu')) customElements.define('annur-mega-menu', AnnurMegaMenu);

document.addEventListener('click', (event) => {
  const closeButton = event.target.closest('.annur-shell__drawer-close');
  if (closeButton) closeButton.closest('.annur-shell__drawer-wrap').open = false;

  const submenuToggle = event.target.closest('[data-annur-mobile-submenu]');
  if (!submenuToggle) return;

  const submenu = document.getElementById(submenuToggle.getAttribute('aria-controls'));
  if (!submenu) return;

  const expanded = submenuToggle.getAttribute('aria-expanded') === 'true';
  submenuToggle.setAttribute('aria-expanded', String(!expanded));
  submenu.hidden = expanded;
});

const setupAnnurMobileDrawers = (root = document) => {
  root.querySelectorAll('.annur-shell__drawer-wrap').forEach((drawer) => {
    if (drawer.dataset.mobileReady === 'true') return;
    drawer.dataset.mobileReady = 'true';
    const toggle = drawer.querySelector(':scope > summary');
    const panel = drawer.querySelector('.annur-shell__drawer');
    if (!toggle || !panel) return;

    let wasOpen = false;
    const focusableElements = () => [
      toggle,
      ...panel.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'),
    ];

    const sync = () => {
      toggle.setAttribute('aria-expanded', String(drawer.open));
      document.body.classList.toggle('annur-mobile-menu-open', drawer.open);
      if (drawer.open) {
        panel.querySelectorAll('img[data-annur-src]').forEach((image) => {
          image.src = image.dataset.annurSrc;
          image.removeAttribute('data-annur-src');
        });
        panel.querySelector('.annur-mobile-nav__search, a, summary')?.focus();
      }
      if (!drawer.open && wasOpen) toggle.focus();
      wasOpen = drawer.open;
    };

    drawer.addEventListener('toggle', sync);
    panel.addEventListener('click', (event) => {
      if (event.target.closest('a')) drawer.open = false;
    });
    drawer.addEventListener('keydown', (event) => {
      if (!drawer.open) return;
      if (event.key === 'Escape') {
        drawer.open = false;
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer && drawer.open) drawer.open = false;
    });
    sync();
  });
};

setupAnnurMobileDrawers();
document.addEventListener('shopify:section:load', (event) => setupAnnurMobileDrawers(event.target));
